import { matchPath, useLocation } from 'react-router-dom';

import useIsInWebView from '~/common/hooks/useIsInWebView';
import { AppNames } from '~/common/models/appNames.model';
import { AppRoutePaths } from '~/common/models/route.model';
import { getNavDisabledPaths } from '~/common/utils/route/route.utils';
import { getAppNameFromPath } from '~/features/navigationDrawer/config/navDrawer.config';

/**
 * Whether the current route should mount the docked Fleet AI
 * Assistant.
 *
 * The dock and its drawer are anchored to the app shell: the drawer
 * offsets itself below the global header and the shell shrinks its
 * main column to make room. Routes without that chrome cannot honor
 * either half of the arrangement, so the dock stays away from them:
 *
 * - Auth pages, which have no signed-in context to answer questions
 *   about.
 * - Every route in `getNavDisabledPaths()` — the terms, migration,
 *   and invite flows that deliberately strip the shell, plus the
 *   mobile-app-required interstitial, which keeps its header only so
 *   an operator can sign out.
 * - WebView, where the native app supplies its own chrome.
 *
 * @returns True when the shell is present and the dock can mount.
 */
export const useIsSidekickDockRoute = (): boolean => {
  const { pathname } = useLocation();
  const { isInWebView } = useIsInWebView();

  const onAuthPage =
    getAppNameFromPath(pathname) === AppNames.AUTH ||
    pathname.startsWith(AppRoutePaths.LOGIN);
  const onChromelessPage = getNavDisabledPaths().some((path) =>
    matchPath(path, pathname)
  );

  return !onAuthPage && !onChromelessPage && !isInWebView;
};
